import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Form, Alert } from "react-bootstrap";
import axios from "axios";
import { API_BASE_URL } from "config.js";
import Swal from "sweetalert2";

function ReactivateNumbersModal({ show, onHide, deactivatedNumbers = [], userCredits = 0, onSuccess }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);

  // Reset selection when modal opens
  useEffect(() => {
    if (show) {
      // Select all by default to make it easy for users
      setSelectedIds(deactivatedNumbers.map(n => n._id));
    }
  }, [show, deactivatedNumbers]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === deactivatedNumbers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(deactivatedNumbers.map(n => n._id));
    }
  };

  const totalCost = selectedIds.length * 200;
  const hasEnoughCredits = userCredits >= totalCost;

  const handleReactivate = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_BASE_URL}/billing/reactivate-numbers`,
        { phoneIds: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.success) {
        await Swal.fire({
          title: "Success!",
          text: res.data.message || `Successfully reactivated ${selectedIds.length} number(s).`,
          icon: "success",
          confirmButtonColor: "#004992"
        });
        if (onSuccess) {
          onSuccess(res.data.credits);
        }
        onHide();
      }
    } catch (error) {
      console.error("Reactivation failed:", error);
      Swal.fire({
        title: "Reactivation Failed",
        text: error.response?.data?.message || "Failed to reactivate numbers.",
        icon: "error",
        confirmButtonColor: "#dc3545"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton style={{ borderBottom: "1px solid #eee" }}>
        <Modal.Title className="fw-bold" style={{ color: "#333" }}>
          Reactivate Phone Numbers
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="p-4">
        <p className="text-muted" style={{ fontWeight: "500" }}>
          Select the deactivated phone numbers you wish to reactivate. Reactivation costs <strong>200 credits ($2.00)</strong> per phone number.
        </p>

        {deactivatedNumbers.length === 0 ? (
          <Alert variant="info" className="fw-bold">No deactivated numbers found.</Alert>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="text-muted fw-bold">
                Available Credits: <strong className="text-primary">{userCredits.toLocaleString()}</strong>
              </span>
              <Button 
                variant="link" 
                className="p-0 text-decoration-none fw-bold" 
                style={{ color: "#004992", fontSize: "0.9rem" }}
                onClick={toggleSelectAll}
              >
                {selectedIds.length === deactivatedNumbers.length ? "Deselect All" : "Select All"}
              </Button>
            </div>

            <div style={{ maxHeight: "250px", overflowY: "auto", border: "1px solid #ddd", borderRadius: "8px" }}>
              <Table hover className="mb-0">
                <thead style={{ background: "#f8f9fa", position: "sticky", top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{ width: "50px", borderBottom: "1px solid #ddd" }}></th>
                    <th style={{ fontWeight: "700", borderBottom: "1px solid #ddd" }}>Phone Number</th>
                    <th style={{ fontWeight: "700", borderBottom: "1px solid #ddd" }}>Friendly Name</th>
                    <th style={{ fontWeight: "700", borderBottom: "1px solid #ddd" }}>Reactivation Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {deactivatedNumbers.map((num) => {
                    const isChecked = selectedIds.includes(num._id);
                    return (
                      <tr 
                        key={num._id} 
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleSelect(num._id)}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <Form.Check 
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSelect(num._id)}
                          />
                        </td>
                        <td className="fw-bold">{num.phoneNumber}</td>
                        <td className="text-muted fw-semibold">{num.friendlyName || "N/A"}</td>
                        <td className="fw-bold text-success">200 credits</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            <div className="mt-4 p-3 bg-light rounded d-flex justify-content-between align-items-center">
              <div>
                <div className="fw-bold" style={{ fontSize: "1.1rem" }}>
                  Total Reactivation Cost: <span style={{ color: "#004992" }}>{totalCost.toLocaleString()} credits</span>
                </div>
                <div className="text-muted fw-semibold" style={{ fontSize: "0.85rem" }}>
                  {selectedIds.length} number(s) selected
                </div>
              </div>
              <div>
                {!hasEnoughCredits && (
                  <span className="text-danger fw-bold me-3">
                    Credits needed: {(totalCost - userCredits).toLocaleString()}
                  </span>
                )}
              </div>
            </div>

            {!hasEnoughCredits && (
              <Alert variant="danger" className="mt-3 fw-bold mb-0">
                ⚠️ Insufficient credits. Please purchase more credits first before reactivating.
              </Alert>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer style={{ borderTop: "1px solid #eee" }}>
        <Button 
          variant="secondary" 
          onClick={onHide} 
          disabled={loading}
          style={{ fontWeight: "600", borderRadius: "6px" }}
        >
          Cancel
        </Button>
        <Button 
          variant="primary"
          onClick={handleReactivate}
          disabled={loading || selectedIds.length === 0 || !hasEnoughCredits}
          style={{ 
            backgroundColor: "#004992", 
            border: "none", 
            fontWeight: "700", 
            borderRadius: "6px",
            padding: "8px 25px"
          }}
        >
          {loading ? "Reactivating..." : `Reactivate Number(s)`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ReactivateNumbersModal;
